import { ThemeColorModes } from '@atlaskit/tokens/theme-config';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { logError } from '../../utils/logger';
import {
	AnnouncementBannerValue,
	CalendarType,
	ChromeExtensionDebugData,
	PluginFeedbackValue,
	SelectedSiteValue,
} from './types';
import { ThemeFileType } from '../theme-client/types';

export enum LocalStorageKey {
	RECENT_WORK_ENABLED = 'recent_work_enabled',
	CALENDAR_ENABLED = 'calendar_enabled',
	CALENDAR_SELECTED = 'calendar_selected',
	SELECTED_SITE = 'selected_site',
	BOOKMARKS_ENABLED = 'bookmark_enabled',
	THEME_COLOR_MODE = 'theme_color_mode',
	ANNOUNCEMENT_BANNER = 'announcement_banner',
	ANNOUNCEMENTS_ENABLED = 'announcements_enabled',
	PLUGIN_FEEDBACK = 'plugin_feedback_usage',
	HAS_USER_ONBOARDED_ON_INSTALL = 'has_user_onboarded_on_install',
	BACKGROUND_PREVIEW_FILE_TYPE = 'background_preview_file_type',
	BACKGROUND_PREVIEW_URL = 'background_preview_url',
	CHROME_EXTENSION_DEBUG_DATA = 'chrome_extension_debug_data',
	IS_FULL_PAGE_CHAT = 'is_full_page_chat',
	WELCOME_HEADER_ENABLED = 'welcome_header_enabled',
	GLOBAL_CREATE_ENABLED = 'global_create_enabled',
	LOCAL_DEV_BANNER_ENABLED = 'local_dev_banner_enabled',
	SELECTED_NTP_AGENT = 'selected_ntp_agent',
	NTP_AGENT_SPOTLIGHT_DISMISSED = 'ntp_agent_spotlight_dismissed',
	NTP_AGENT_DROPDOWN_OPEN_COUNT = 'ntp_agent_dropdown_open_count',
}

/**
 * Values must be valid JSON
 */
type KeyValueMap = {
	[LocalStorageKey.RECENT_WORK_ENABLED]: boolean;
	[LocalStorageKey.CALENDAR_ENABLED]: boolean;
	[LocalStorageKey.SELECTED_SITE]: SelectedSiteValue | undefined;
	[LocalStorageKey.BOOKMARKS_ENABLED]: boolean;
	[LocalStorageKey.THEME_COLOR_MODE]: ThemeColorModes;
	[LocalStorageKey.ANNOUNCEMENT_BANNER]: AnnouncementBannerValue;
	[LocalStorageKey.ANNOUNCEMENTS_ENABLED]: boolean;
	[LocalStorageKey.PLUGIN_FEEDBACK]: PluginFeedbackValue;
	[LocalStorageKey.CALENDAR_SELECTED]: CalendarType;
	[LocalStorageKey.HAS_USER_ONBOARDED_ON_INSTALL]: boolean;
	[LocalStorageKey.BACKGROUND_PREVIEW_FILE_TYPE]: ThemeFileType | undefined;
	[LocalStorageKey.BACKGROUND_PREVIEW_URL]: string | undefined;
	[LocalStorageKey.CHROME_EXTENSION_DEBUG_DATA]: ChromeExtensionDebugData;
	[LocalStorageKey.IS_FULL_PAGE_CHAT]: boolean;
	[LocalStorageKey.WELCOME_HEADER_ENABLED]: boolean;
	[LocalStorageKey.GLOBAL_CREATE_ENABLED]: boolean;
	[LocalStorageKey.LOCAL_DEV_BANNER_ENABLED]: boolean;
	[LocalStorageKey.SELECTED_NTP_AGENT]: Agent | undefined;
	[LocalStorageKey.NTP_AGENT_SPOTLIGHT_DISMISSED]: boolean;
	[LocalStorageKey.NTP_AGENT_DROPDOWN_OPEN_COUNT]: number;
};

export const setLocalStorageItem = <K extends keyof KeyValueMap>(
	key: K,
	value: KeyValueMap[K],
): void => {
	try {
		const valueString = value !== undefined ? JSON.stringify(value) : '';
		localStorage.setItem(key, valueString);
	} catch (error) {
		logError({ error, impact: 'Failed to save to local storage', extra: { key } });
	}
};

export const getLocalStorageItem = <K extends keyof KeyValueMap>(
	key: K,
	defaultValue?: KeyValueMap[K],
): KeyValueMap[K] | null => {
	try {
		const valueString = localStorage.getItem(key);

		if (!valueString && defaultValue !== undefined) {
			setLocalStorageItem(key, defaultValue);
			return defaultValue;
		}

		return valueString && JSON.parse(valueString);
	} catch (error) {
		logError({ error, impact: 'Failed to retrieve from local storage', extra: { key } });
		return null;
	}
};

export const addLocalStorageEventListener = <K extends keyof KeyValueMap>(
	key: K,
	callback: (value: KeyValueMap[K]) => void,
): (() => void) => {
	const callbackHandle = (event: StorageEvent): void => {
		if (event.key !== key) {
			return;
		}

		try {
			const value = event.newValue ? JSON.parse(event.newValue) : undefined;
			callback(value);
		} catch (error) {
			logError({ error, impact: 'Failed to parse storage event value', extra: { key } });
		}
	};

	const customEventCallbackHandle = (event: CustomEvent): void => {
		if (event.detail.key !== key) {
			return;
		}

		try {
			callback(event.detail.newValue);
		} catch (error) {
			logError({ error, impact: 'Failed to parse custom storage event value', extra: { key } });
		}
	};

	window.addEventListener('storage', callbackHandle);
	window.addEventListener('STORAGE_SAME_WINDOW', customEventCallbackHandle as EventListener);

	const destroyStorageListener = (): void => removeLocalStorageEventListener(callbackHandle);
	const destroyCustomEventListener = (): void =>
		removeCustomEventListener(customEventCallbackHandle);

	return () => {
		destroyStorageListener();
		destroyCustomEventListener();
	};
};

export const removeLocalStorageEventListener = (
	callbackHandle: (event: StorageEvent) => void,
): void => {
	window.removeEventListener('storage', callbackHandle);
};

const removeCustomEventListener = (callbackHandle: (event: CustomEvent) => void): void => {
	window.removeEventListener('STORAGE_SAME_WINDOW', callbackHandle as EventListener);
};

export const clearLocalStorage = (): void => {
	localStorage.clear();
};

/**
 * LEGACY ONLY!
 * Prefer using setLocalStorageItem with valid JSON values instead of this function
 */
export const setLocalStorageItemRawString = (key: string, value: string): void => {
	try {
		localStorage.setItem(key, value);
	} catch (error) {
		logError({ error, impact: 'Failed to save raw string to local storage', extra: { key } });
	}
};

/**
 * LEGACY ONLY!
 * Prefer using getLocalStorageItem with valid JSON values instead of this function
 */
export const getLocalStorageItemRawString = (key: string): string | null => {
	try {
		return localStorage.getItem(key);
	} catch (error) {
		logError({ error, impact: 'Failed to retrieve raw string from local storage', extra: { key } });
		return null;
	}
};

/**
 * LEGACY ONLY!
 * Prefer using addLocalStorageEventListener with valid JSON values instead of this function
 */
export const addLocalStorageEventListenerRawString = (
	key: string,
	callback: (value: string) => void,
): ((event: StorageEvent) => void) => {
	const callbackHandle = (event: StorageEvent): void => {
		if (event.key !== key || !event.newValue) {
			return;
		}

		callback(event.newValue);
	};

	window.addEventListener('storage', callbackHandle);

	return callbackHandle;
};
